import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Check, X, RotateCcw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";

const FILTERS = [["pending", "Pending"], ["approved", "Published"], ["rejected", "Rejected"], ["", "All"]];

export function ReviewsAdmin() {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {} });
  const [error, setError] = useState("");
  const load = () => api.get("/admin/reviews", { params: filter ? { status: filter } : {} }).then((r) => { setData(r.data); setError(""); }).catch((e) => setError(e.response?.data?.detail || "Could not load reviews."));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = async (id, status) => {
    try { await api.put(`/admin/reviews/${id}`, { status }); toast.success(status === "approved" ? "Published on the homepage." : status === "rejected" ? "Rejected." : "Back to pending."); load(); } catch { toast.error("Could not update."); }
  };
  return (
    <div className="space-y-4" data-testid="admin-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Names show as first name + last initial.</p>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1">
          {FILTERS.map(([id, l]) => <button key={id} type="button" onClick={() => setFilter(id)} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`admin-reviews-filter-${id || "all"}`}>{l}{id && data.counts[id] != null ? ` · ${data.counts[id]}` : ""}</button>)}
        </div>
      </div>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-4" data-testid="admin-reviews-error">{String(error)}</p>}
      {!error && data.items.length === 0 && <p className="text-sm text-slate-400 italic bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-reviews-empty">No reviews here.</p>}
      <ul className="space-y-3">
        {data.items.map((r) => (
          <li key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="inline-flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>
                  <b className="text-slate-800">{r.display_name}</b> · {r.email}{r.role && <> · {r.role}</>} · {new Date(r.updated_at).toLocaleDateString()}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{r.status === "approved" ? "published" : r.status}</span>
                </div>
                <p className="mt-2 text-sm text-slate-800">“{r.text}”</p>
              </div>
              <div className="flex gap-2 shrink-0">
                {r.status !== "approved" && <Button size="sm" onClick={() => set(r.id, "approved")} className="bg-green-600 hover:bg-green-700" data-testid={`admin-review-approve-${r.id}`}><Check className="w-4 h-4 mr-1" /> Publish</Button>}
                {r.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => set(r.id, "rejected")} data-testid={`admin-review-reject-${r.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>}
                {r.status !== "pending" && <Button size="sm" variant="ghost" onClick={() => set(r.id, "pending")} data-testid={`admin-review-pending-${r.id}`}><RotateCcw className="w-4 h-4 mr-1" /> Unpublish</Button>}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
